import { useEffect, useRef, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
	confirmSignUp,
	resendVerificationCode,
	signIn,
} from "src/components/organisms/auth";
import {
	ILoggedInUser,
	useAuth,
} from "src/components/organisms/auth/auth-context";
import { TokenHelper } from "../../auth/token-helper";
import LoadingModal from "src/components/molecules/loading/LoadingModal";
import ButtonPro from "src/components/atoms/primary-button/primary-button";
import { PermissionBagService } from "../permissionBagService";
import OTPInput from "src/components/molecules/otp-input/otp-input";
import Alert from "src/components/atoms/alert/alert";
import LogoutLinkButton from "src/components/molecules/logout-button/logout-link-button";
import { EnvelopeIcon } from "@heroicons/react/24/solid";

interface EmailVerificationProps {
	emailAddress: string;
	onVerification: (isSuccess: boolean) => void;
}

const EmailVerification: React.FC<EmailVerificationProps> = ({
	emailAddress,
	onVerification,
}) => {
	const [verificationCode, setVerificationCode] = useState("");
	const [error, setError] = useState("");
	const [resultMessage, setResultMessage] = useState("");
	const [loading, setLoading] = useState(false);
	const location = useLocation();
	const navigate = useNavigate();
	const tokenHelper = useRef<TokenHelper | null>(null);
	const permissionBag = useRef<PermissionBagService | null>(null);
	const { login, setCognitoUserContext: setCognitoUser } = useAuth();

	// Persist credentials using useMemo
	const businessEmail = useMemo(
		() => location.state?.businessEmail,
		[location]
	);
	const password = useMemo(() => location.state?.password, [location]);

	useEffect(() => {
		tokenHelper.current = new TokenHelper();
	}, []);

	const handleResendVerificationCode = () => {
		if (businessEmail) {
			setError("");
			setResultMessage("");
			resendVerificationCode(businessEmail, (err, result) => {
				if (err) {
					setError(err.message || JSON.stringify(err));
				} else if (result) {
					setResultMessage(
						result.message || "Verification code resent."
					);
				}
			});
		}
	};

	const handleVerify = async (event: React.FormEvent) => {
		event.preventDefault();
		setError("");
		if (!businessEmail || !password) {
			setError("Missing email or password.");
			navigate("/login");
			return;
		}

		setLoading(true);
		confirmSignUp(businessEmail, verificationCode, async (err) => {
			if (err) {
				setError(err.message || JSON.stringify(err));
				setLoading(false);
				return;
			}

			signIn(businessEmail, password, async (result) => {
				setLoading(false);
				switch (result.status) {
					case "SUCCESS": {
						try {
							const accessToken = result.session
								.getAccessToken()
								.getJwtToken();
							const tokenProps =
								await tokenHelper.current?.decodeToken(
									accessToken
								);
							permissionBag.current = new PermissionBagService(
								accessToken
							);
							const permissions =
								await permissionBag.current.getPermissionBag(
									result.session.getAccessToken().payload.role
								);

							if (!permissions?.permissions.length) {
								setError("Unable to get user permissions.");
								navigate("/error", {
									state: { error: "Missing permissions" },
								});
								return;
							}

							const loggedInUser: ILoggedInUser = {
								id: result.session.getIdToken().payload.sub,
								givenName:
									result.session.getIdToken().payload
										.given_name,
								familyName:
									result.session.getIdToken().payload
										.family_name,
								username: businessEmail,
								phoneNumber:
									result.session.getIdToken().payload
										.phone_number,
								ex_user_id:
									result.session.getAccessToken().payload
										.ex_user_id,
								accessToken,
								num: result.session.getAccessToken().payload
									.num,
								accountId: tokenProps?.account_id ?? "",
								email: tokenProps?.email ?? "",
								reseller_id: tokenProps?.reseller_id,
								role: tokenProps?.role,
								exp: result.session.getAccessToken().payload
									.exp,
								permissionBag: permissions,
								has_multiple_accounts:
									tokenProps?.has_multiple_accounts ?? false,
							};

							login(loggedInUser);
							if (!loggedInUser.accountId)
								navigate("/select-account");
							else navigate("/");
						} catch (err) {
							console.error("Token decode error:", err);
							setError(
								"Login succeeded, but token handling failed."
							);
						}
						break;
					}

					case "NEW_PASSWORD_REQUIRED":
						setCognitoUser(result.cognitoUser);
						navigate("/set-new-password", {
							state: { businessEmail },
						});
						break;

					case "CHALLENGE": {
						setCognitoUser(result.cognitoUser);
						const { challengeName, challengeParameters } = result;
						switch (challengeName) {
							case "CUSTOM_CHALLENGE":
								navigate("/verify-email-otp", {
									state: {
										businessEmail,
										challengeParameters,
									},
								});
								break;
							case "SOFTWARE_TOKEN_MFA":
								navigate("/verify-totp", {
									state: {
										businessEmail,
										challengeParameters,
									},
								});
								break;
							case "SELECT_MFA_TYPE":
								navigate("/select-mfa", {
									state: {
										businessEmail,
										challengeParameters,
									},
								});
								break;
							case "MFA_SETUP":
								navigate("/setup-mfa", {
									state: {
										businessEmail,
										challengeParameters,
									},
								});
								break;
							default:
								setError(
									`Unsupported challenge: ${challengeName}`
								);
						}
						break;
					}

					case "ERROR":
						if (result.error.code === "UserNotConfirmedException") {
							navigate("/verify", {
								state: { businessEmail, password },
							});
						} else {
							setError(
								result.error.message ||
									JSON.stringify(result.error)
							);
						}
						break;
				}
			});
		});
	};

	return (
		<div className="flex flex-col items-center justify-center gap-8 w-full md:px-0">
			<label className="font-Poppins tracking-wide font-medium flex-wrap text-center">
				If you don&apos;t find the email in your inbox, please check
				your junk or spam folder.
			</label>

			{businessEmail && (
				<div className="flex flex-col items-center gap-2 font-Poppins tracking-wider py-2 px-4 text-gray-800 font-semibold w-fit">
					<EnvelopeIcon className="w-6 h-6 text-gray-900"></EnvelopeIcon>
					{businessEmail}
				</div>
			)}

			<form
				className="flex flex-col items-center justify-center gap-6 w-full"
				onSubmit={handleVerify}
			>
				<span className="ms-2 text-md font-semibold">
					Authorisation code
				</span>
				<OTPInput
					numDigits={6}
					onComplete={(otp) => setVerificationCode(otp)}
				/>
				<label className="ms-2 text-md font-semibold text-white md:text-gray-900">
					<button
						className="text-blue-500 underline"
						onClick={handleResendVerificationCode}
					>
						Resend verification code
					</button>
				</label>
				<ButtonPro label="Verify" type="submit" />
				<LogoutLinkButton></LogoutLinkButton>
			</form>

			{error && <Alert text={error} type="error" />}
			{resultMessage && <Alert text={resultMessage} type="info" />}

			<LoadingModal
				open={loading}
				message="Please wait, while we verify your code"
			/>
		</div>
	);
};

export default EmailVerification;
